import React, { FC } from 'react';

import { NumericFormat } from 'react-number-format';
import { twMerge } from 'tailwind-merge';

import { Label } from '../../label';
import { IAmountNumberInput, INumberAutosizeInput } from './types';

// Sized by CSS field-sizing rather than a measuring component, so the input
// tracks its own content.
const NumberAutosizeInput = React.forwardRef<
  HTMLInputElement,
  INumberAutosizeInput
>(
  (
    {
      className,
      isInvalid,
      inputClassName,
      placeholder,
      value,
      onChange,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <input
        ref={ref}
        {...props}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={
          placeholder && !isNaN(Number(placeholder)) ? placeholder : '0'
        }
        className={twMerge(
          'min-w-0 max-w-full overflow-hidden appearance-none border-none bg-[transparent] shadow-none outline-none [field-sizing:content] placeholder:text-base-100',
          disabled
            ? 'opacity-50'
            : isInvalid && 'text-negative-500 placeholder:text-negative-500',
          inputClassName,
          className,
        )}
      />
    );
  },
);

export const AmountNumberInput: FC<IAmountNumberInput> = ({
  value,
  onValueChange,
  className,
  prefix,
  suffix,
  numberInputProps = {},
  numberAutosizeInputProps = {},
  isInvalid,
  wrapperClassName,
  size = 'default',
  ...props
}) => {
  return (
    <div
      className={twMerge(
        'flex w-full items-center justify-center overflow-hidden',
        wrapperClassName,
      )}
    >
      <Label
        className={twMerge(
          'flex w-max items-center justify-center gap-1 overflow-hidden uppercase',
          size === 'default' && 'heading-s',
          size === 'large' && 'heading-l',
          className,
        )}
      >
        {prefix}
        <NumericFormat
          value={value}
          onValueChange={({ value }) => {
            onValueChange?.(value);
          }}
          isInvalid={isInvalid}
          {...numberAutosizeInputProps}
          customInput={NumberAutosizeInput}
          allowNegative={numberInputProps?.allowNegative ?? false}
          decimalScale={numberInputProps?.decimalScale ?? 2}
          thousandSeparator={numberInputProps?.thousandSeparator ?? ' '}
          decimalSeparator={numberInputProps?.decimalSeparator ?? '.'}
          allowedDecimalSeparators={
            numberInputProps?.allowedDecimalSeparators ?? ['.', ',']
          }
          {...numberInputProps}
          {...props}
        />
        {suffix}
      </Label>
    </div>
  );
};
